import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import DetailsLoader, {detailsLoaderQuery} from '../../loaders/DetailsLoader';
import {LoadMore} from '../../../../../loaders/LoadMore';
import {BudgetEyeOptions} from '../../BudgetPage.EyeOptions';
import DetailsTable from './DetailsTable';
import ActualDetailsTable from './ActualDetailsTable';
import {BUDGET_VIEWS} from '../../../../../../constants';
import {BudgetTooltips} from '../../util/BudgetTooltips';
import {filterAggregateFields} from '../../util/BudgetUtils';
import {useRemappingFormatMessage} from '../../../../../../forecast-app/shared/util/FinancialInternationalisationUtil';
import {BUDGET_TYPE} from '../../../../../../constants';
import {hasFeatureFlag} from '../../../../../../forecast-app/shared/util/FeatureUtil';
import CompanySetupUtil from '../../../../../../forecast-app/shared/util/CompanySetupUtil';

const Details = ({company, project, selectedAggregateLevel, selectedViewType, eyeOptionMap}) => {
	const isFixedPrice = project.budgetType === BUDGET_TYPE.FIXED_PRICE || project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const useFixedPriceCalculation = isFixedPrice && project.useFixedPriceForBaselineRevenue;
	const formatMessage = useRemappingFormatMessage();

	const {useBaseline} = project;

	const hasExpenses = CompanySetupUtil.hasFinance();
	const settings = project.financialSourceSettings;

	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const useNewTooltips =
		hasFinancialCategoriesUpdate &&
		(isFixedPrice ||
			project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS ||
			(project.budgetType === BUDGET_TYPE.RETAINER &&
				project.defaultPeriodBudgetType === BUDGET_TYPE.TIME_AND_MATERIALS));

	const baselineAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? ['baselineRevenue', 'baselineCost', 'baselineProfit', 'baselineMargin', 'baselineTimeAndExpenses']
			: ['baselineRevenue', 'baselineCost', 'baselineProfit']
	);
	const baselineVsActualAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'baselineVsActualRevenueRecognition',
					'baselineVsActualCost',
					'baselineVsActualRevenueProfit',
					'baselineVsActualRevenueMargin',
					'baselineVsBillableActualTimeAndExpenses',
			  ]
			: ['baselineVsBillableActualTimeAndExpenses', 'baselineVsActualCost', 'baselineVsActualProfit']
	);
	const baselineVsForecastAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'baselineVsTotalRevenueAtCompletion',
					'baselineVsTotalCostAtCompletion',
					'baselineVsTotalRevenueProfitAtCompletion',
					'baselineVsTotalRevenueMarginAtCompletion',
					'baselineVsBillableTotalTimeAndExpensesAtCompletion',
			  ]
			: [
					'baselineVsBillableTotalTimeAndExpensesAtCompletion',
					'baselineVsTotalCostAtCompletion',
					'baselineVsTotalProfitAtCompletion',
			  ]
	);
	const actualAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'totalActualRevenueRecognition',
					'actualCost',
					'actualRevenueProfit',
					'actualRevenueMargin',
					'billableActualTimeAndExpenses',
			  ]
			: ['billableActualTimeAndExpenses', 'actualCost', 'actualProfit']
	);
	const planAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'plannedRevenue',
					'plannedCost',
					'plannedRevenueProfit',
					'plannedRevenueMargin',
					'billablePlannedTimeAndExpenses',
			  ]
			: ['billablePlannedTimeAndExpenses', 'plannedCost', 'plannedProfit']
	);
	const planVsActualAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'planVsActualRevenueRecognition',
					'planVsActualCost',
					'planVsActualRevenueProfit',
					'planVsActualRevenueMargin',
					'planVsBillableActualTimeAndExpenses',
			  ]
			: ['planVsBillableActualTimeAndExpenses', 'planVsActualCost', 'planVsActualProfit']
	);
	const remainingAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'totalForecastRevenueToComplete',
					'forecastCostToComplete',
					'forecastRevenueProfitToComplete',
					'forecastRevenueMarginToComplete',
					'billableForecastTimeAndExpensesToComplete',
			  ]
			: ['billableForecastTimeAndExpensesToComplete', 'forecastCostToComplete', 'forecastProfitToComplete']
	);
	const forecastAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'totalRevenueRecognition',
					'totalCostAtCompletion',
					'totalRevenueProfitAtCompletion',
					'totalRevenueMarginAtCompletion',
					'billableTotalTimeAndExpensesAtCompletion',
			  ]
			: ['billableTotalTimeAndExpensesAtCompletion', 'totalCostAtCompletion', 'totalProfitAtCompletion']
	);
	const planVsForecastAggregates = filterAggregateFields(
		project,
		hasFinancialCategoriesUpdate
			? [
					'planVsTotalRevenueAtCompletion',
					'planVsTotalCostAtCompletion',
					'planVsTotalRevenueProfitAtCompletion',
					'planVsTotalRevenueMarginAtCompletion',
					'planVsTotalBillableTimeAndExpensesAtCompletion',
			  ]
			: ['planVsTotalBillableTimeAndExpensesAtCompletion', 'planVsTotalCostAtCompletion', 'planVsTotalProfitAtCompletion']
	);

	const aggregates = [
		...baselineAggregates,
		...baselineVsActualAggregates,
		...baselineVsForecastAggregates,
		...actualAggregates,
		...planAggregates,
		...planVsActualAggregates,
		...remainingAggregates,
		...forecastAggregates,
		...planVsForecastAggregates,
	];

	return (
		<>
			<LoadMore
				key="query-render-details"
				query={detailsLoaderQuery}
				variables={{
					projectId: project.id,
					startYear: project.projectFirstDateYear,
					startMonth: project.projectFirstDateMonth,
					startDay: project.projectFirstDateDay,
					endYear: project.projectLastDateYear,
					endMonth: project.projectLastDateMonth,
					endDay: project.projectLastDateDay,
					aggregateLevel: selectedAggregateLevel,
					aggregates: aggregates,
				}}
				loader={<DetailsLoader aggregateLevel={selectedAggregateLevel} aggregates={aggregates} />}
			>
				{({data}) => {
					return (
						<>
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE] && (
								<DetailsTable
									key={`baseline-${selectedAggregateLevel}`}
									cy={'details-baseline'}
									title={formatMessage({id: 'baseline.baseline'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineAggregates}
									selectedViewType={selectedViewType}
									tooltips={BudgetTooltips.BaselineTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										isFixedPrice,
										useFixedPriceCalculation,
										hasFinancialCategoriesUpdate
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.PLAN] && (
								<DetailsTable
									key={`plan-${selectedAggregateLevel}`}
									cy={'details-planned'}
									title={formatMessage({id: 'common.planned'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={planAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.PlanTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										settings,
										hasFinancialCategoriesUpdate,
										useNewTooltips
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.ACTUAL] && (
								<ActualDetailsTable
									key={`actual-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.actual'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									actualAggregates={actualAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.ActualTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										settings,
										hasFinancialCategoriesUpdate,
										useNewTooltips
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.REMAINING] && (
								<DetailsTable
									key={`remaining-${selectedAggregateLevel}`}
									cy={'details-forecast_to_complete'}
									title={formatMessage({
										id: 'project_budget.forecast_to_complete',
									})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={remainingAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.RemainingTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										settings,
										hasFinancialCategoriesUpdate,
										useNewTooltips
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.FORECAST] && (
								<DetailsTable
									key={`forecast-${selectedAggregateLevel}`}
									cy={'details-total_at_completion'}
									title={formatMessage({
										id: 'project_budget.total_at_completion',
									})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={forecastAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.ForecastTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										settings,
										hasFinancialCategoriesUpdate,
										useNewTooltips
									)}
								/>
							)}
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE_VS_ACTUAL] && (
								<DetailsTable
									key={`baseline-vs-actual-${selectedAggregateLevel}`}
									cy={'details-baseline_vs_actual'}
									title={formatMessage({id: 'project_budget.baseline_vs_actual'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineVsActualAggregates}
									selectedViewType={selectedViewType}
									tooltips={BudgetTooltips.BaselineVsActualTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										hasFinancialCategoriesUpdate
									)}
								/>
							)}
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE_VS_FORECAST] && (
								<DetailsTable
									key={`baseline-vs-forecast-${selectedAggregateLevel}`}
									cy={'details-baseline_vs_forecast'}
									title={formatMessage({id: 'project_budget.baseline_vs_forecast'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineVsForecastAggregates}
									selectedViewType={selectedViewType}
									tooltips={BudgetTooltips.BaselineVsForecastTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										hasFinancialCategoriesUpdate
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.PLAN_VS_ACTUAL] && (
								<DetailsTable
									key={`actualVsPlan-${selectedAggregateLevel}`}
									cy={'details-plan_vs_actual'}
									title={formatMessage({id: 'project_budget.plan_vs_actual'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={planVsActualAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.PlanVsActualTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										hasExpenses,
										hasFinancialCategoriesUpdate
									)}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.PLAN_VS_FORECAST] && (
								<DetailsTable
									key={`planVsForecast-${selectedAggregateLevel}`}
									cy={'details-plan_vs_forecast'}
									title={formatMessage({id: 'project_budget.plan_vs_forecast'})}
									company={company}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={planVsForecastAggregates}
									selectedViewType={selectedViewType}
									data={data}
									tooltips={BudgetTooltips.PlanVsForecastTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										hasFinancialCategoriesUpdate
									)}
								/>
							)}
						</>
					);
				}}
			</LoadMore>
		</>
	);
};

export default createFragmentContainer(Details, {
	company: graphql`
		fragment Details_company on Company {
			id
			isUsingProjectAllocation
		}
	`,
	project: graphql`
		fragment Details_project on ProjectType {
			id
			useBaseline
			budgetType
			defaultPeriodBudgetType
			projectFirstDateYear
			projectFirstDateMonth
			projectFirstDateDay
			projectLastDateYear
			projectLastDateMonth
			projectLastDateDay
			estimationUnit
			billable
			...DetailsTable_project
			...ActualDetailsTable_project
			financialSourceSettings {
				plannedHours
				plannedRevenue
				plannedCost
				forecastHours
				forecastRevenue
				forecastCost
				actualHours
				actualRevenue
				actualCost
			}
		}
	`,
});
